"use client";

import { useUser } from "@clerk/nextjs";
import {
  parsePushPromptDecision,
  pushPromptStorageKey,
  shouldOfferPushPrompt,
  type PushEnableError,
  type PushPromptDecision,
  type PushPromptPermission,
} from "@award-party/shared";
import { Bell } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAnalyticsConsent } from "@/lib/use-analytics-consent";
import { capturePostHogEvent } from "@/lib/posthog-capture";
import {
  createBrowserPushClient,
  isNativePushShell,
  readInjectedPushToken,
  waitForNativePushToken,
} from "@/lib/push-browser";

const NATIVE_PROMPT_DELAY_MS = 800;
const WEB_PROMPT_DELAY_MS = 4000;
const NATIVE_TOKEN_WAIT_MS = 1500;

function readWebPushPermission(): PushPromptPermission {
  if (
    typeof Notification === "undefined" ||
    !("serviceWorker" in navigator) ||
    !("PushManager" in window)
  ) {
    return "unsupported";
  }
  if (
    Notification.permission === "granted" ||
    Notification.permission === "denied"
  ) {
    return Notification.permission;
  }
  return "default";
}

function anotherDialogIsOpen(): boolean {
  return Boolean(document.querySelector('[role="dialog"][data-state="open"]'));
}

export function PushPrompt() {
  const t = useTranslations("Push");
  const locale = useLocale();
  const pathname = usePathname();
  const { user, isLoaded } = useUser();
  const { shouldShowBanner: cookieBannerVisible, isReady: consentReady } =
    useAnalyticsConsent();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<PushEnableError | null>(null);
  const outcomeRef = useRef<PushPromptDecision | null>(null);
  const userId = user?.id ?? null;

  useEffect(() => {
    if (
      !isLoaded ||
      !userId ||
      !consentReady ||
      cookieBannerVisible ||
      pathname === "/settings/notifications"
    ) {
      return;
    }

    const accountId = userId;
    const nativeShell = isNativePushShell();
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        let nativeTokenPresent = Boolean(readInjectedPushToken());
        if (nativeShell && !nativeTokenPresent) {
          nativeTokenPresent = Boolean(
            await waitForNativePushToken(NATIVE_TOKEN_WAIT_MS),
          );
        }
        if (
          cancelled ||
          anotherDialogIsOpen() ||
          !shouldOfferPushPrompt({
            decision: parsePushPromptDecision(
              readStoredDecision(pushPromptStorageKey(accountId)),
            ),
            permission: readWebPushPermission(),
            nativeShell,
            nativeTokenPresent,
          })
        ) {
          return;
        }
        setOpen(true);
        capturePostHogEvent("push_prompt_viewed", {
          surface: nativeShell ? "native" : "web",
        });
      })();
    }, nativeShell ? NATIVE_PROMPT_DELAY_MS : WEB_PROMPT_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [consentReady, cookieBannerVisible, isLoaded, pathname, userId]);

  function remember(decision: PushPromptDecision) {
    if (!userId || outcomeRef.current) {
      return;
    }
    outcomeRef.current = decision;
    try {
      localStorage.setItem(pushPromptStorageKey(userId), decision);
    } catch {
      // Private mode can block storage. The prompt may appear again.
    }
  }

  function dismiss() {
    remember("dismissed");
    setOpen(false);
    capturePostHogEvent("push_prompt_dismissed");
  }

  async function enable() {
    if (!userId || pending) {
      return;
    }
    setPending(true);
    setError(null);
    try {
      const result = await createBrowserPushClient(locale).enable();
      if (!result.ok) {
        if (result.error === "denied") {
          remember("dismissed");
          setOpen(false);
          capturePostHogEvent("push_prompt_denied");
          return;
        }
        setError(result.error);
        return;
      }
      remember("accepted");
      setOpen(false);
      capturePostHogEvent("push_prompt_accepted");
    } catch {
      setError("failed");
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next || pending || outcomeRef.current) {
          return;
        }
        dismiss();
      }}
    >
      <DialogContent className="rounded-3xl border-(--mk-cream-08) bg-(--mk-ink-soft) p-8 text-center text-(--mk-cream) sm:max-w-[420px]">
        <div className="flex justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-(--mk-gold-border) bg-(--mk-gold-bg)">
            <Bell className="h-6 w-6 text-(--mk-gold)" aria-hidden="true" />
          </div>
        </div>
        <DialogTitle className="font-serif text-2xl font-black leading-tight text-balance text-(--mk-cream)">
          {t("promptTitle")}
        </DialogTitle>
        <DialogDescription className="text-sm leading-relaxed text-(--mk-cream-60)">
          {t("promptBody")}
        </DialogDescription>
        <div className="mt-2 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => void enable()}
            disabled={pending}
            className="flex h-12 w-full cursor-pointer items-center justify-center rounded-2xl bg-(--mk-gold) text-[15px] font-bold text-(--mk-ink) transition-colors hover:bg-(--mk-gold-light) disabled:cursor-progress disabled:opacity-60"
          >
            {pending ? t("enabling") : t("enable")}
          </button>
          <button
            type="button"
            onClick={dismiss}
            disabled={pending}
            className="flex h-12 w-full cursor-pointer items-center justify-center rounded-2xl text-sm font-semibold text-(--mk-cream-60) transition-colors hover:text-(--mk-cream) disabled:cursor-progress disabled:opacity-60"
          >
            {t("promptNotNow")}
          </button>
        </div>
        {error ? (
          <p className="text-sm text-red-300">{enableErrorMessage(t, error)}</p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function readStoredDecision(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function enableErrorMessage(
  t: ReturnType<typeof useTranslations<"Push">>,
  error: PushEnableError,
): string {
  switch (error) {
    case "unsupported":
      return t("errorUnsupported");
    case "denied":
      return t("errorDenied");
    case "notConfigured":
      return t("errorNotConfigured");
    case "failed":
      return t("errorFailed");
  }
}
